import classnames from "classnames";
import { componentWrapper } from "../../../utilities";
import { Box } from "../../box";
import { Divider } from "../../divider";
import { Group } from "../../group";
import { Pill } from "../../pill";
import { Stack } from "../../stack";
import { Text } from "../../text";
import { Tooltip } from "../../tooltip";
import styles from "./variable-pill.module.scss";
import type { CustomVariablePillFactory, IProps } from "./variable-pill.types";

/**
 * Display-only pill for an Ask Spotlight variable: the standard `Pill` with a violet label, an optional kind
 * icon, a selection ring and an optional hover tooltip. It holds no variable state, so it also works outside the
 * composer, e.g. as the `subtle` variant inside a sent message bubble.
 * @param props.label The variable's display label.
 * @param props.suffix Optional text after the label that stays in full while the label truncates.
 * @param props.icon Optional leading icon for the variable's kind.
 * @param props.tooltip Optional hover tooltip; its `items` go below a divider.
 * @param props.selected Whether to draw the selection ring, which also hides the tooltip. Defaults to false.
 * @param props.empty Whether to draw the grey placeholder look of a variable with no value yet. Defaults to false.
 * @param props.variant The `Pill` variant to draw on. Defaults to "default"; the message bubble uses "subtle".
 * @param props.size The `Pill` size. Defaults to "sm", which is what the composer draws.
 * @returns {JSX.Element} The VariablePill component.
 */
const VariablePill = componentWrapper<CustomVariablePillFactory, IProps>(
    "VariablePill",
    (
        {
            label,
            suffix,
            icon,
            tooltip,
            selected = false,
            empty = false,
            variant = "default",
            size = "sm",
            className,
            ...otherProps
        },
        ref
    ) => {
        const pill = (
            <Pill
                ref={ref}
                className={classnames(styles["root"], className)}
                variant={variant}
                size={size}
                leftIcon={icon}
                data-selected={selected}
                data-empty={empty}
                data-testid="variable-pill"
                {...otherProps}
            >
                {suffix === undefined ? (
                    label
                ) : (
                    // Spans, since the pill's text is a paragraph. The label is what shrinks, so the suffix stays whole.
                    <Group renderRoot={(props) => <span {...props} />} wrap="nowrap" gap={0}>
                        <Box component="span" className={styles["label"]} miw={0}>
                            {label}
                        </Box>
                        <Box component="span" className={styles["suffix"]} flex="none">
                            {suffix}
                        </Box>
                    </Group>
                )}
            </Pill>
        );

        if (tooltip === undefined) {
            return pill;
        }

        const { prefix, title, items } = tooltip;
        const header = (
            <Text inherit>
                {prefix !== undefined && <Text span inherit dimmed>{`${prefix}: `}</Text>}
                {title}
            </Text>
        );

        const tooltipLabel =
            items === undefined ? (
                header
            ) : (
                <Stack gap="sm">
                    {header}
                    <Divider color="var(--border-transparent-default-2-color)" />
                    <Stack gap="xs">
                        {items.map((item, index) => (
                            <Text key={`${index}-${item}`} inherit>
                                {item}
                            </Text>
                        ))}
                    </Stack>
                </Stack>
            );

        return (
            <Tooltip label={tooltipLabel} disabled={selected}>
                {pill}
            </Tooltip>
        );
    }
);

export { VariablePill };
